import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

const CONTACT_EMAIL = 'support@asmakam.com';
const SUPPORT_PHONE = '508-202-0122';
const BUSINESS_ADDRESS = '15 Common St, #488, Natick, MA 01760';

export default function Privacy() {
	return (
		<div className='bg-slate-950 text-white min-h-screen'>
			<Nav />
			<main className='mx-auto max-w-4xl px-6 py-20'>
				<h1 className='font-display text-3xl sm:text-5xl font-semibold tracking-tight text-white'>
					Privacy Policy
				</h1>
				<p className='mt-4 text-base text-blue-200/70'>
					Effective date: 19 September 2025 • Last updated: 19 September 2025
				</p>
				<div className='mt-6 text-base text-blue-100/90'>
					Learn how we collect, use, and protect personal information when you
					use Asmakam LLC products and services, including the CrikIT platform.
					Please also see our{' '}
					<Link
						to='/terms'
						className='text-blue-400 underline hover:text-blue-300'>
						Terms & Conditions
					</Link>
					.
				</div>

				<div className='mt-12 text-lg text-blue-100/80 leading-relaxed space-y-12'>
					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>1) Scope</h2>
						<p className='mb-6'>
							This policy describes how Asmakam LLC ("Asmakam", "we", "us")
							handles personal information when you visit our websites, use the
							CrikIT platform, interact with associated mobile apps, or opt in
							to SMS communications.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							2) Information We Collect
						</h2>
						<ul className='space-y-4 mb-6 list-disc list-inside'>
							<li>
								<strong className='text-white'>Account & contact data:</strong>{' '}
								name, email, mobile number, organization, role.
							</li>
							<li>
								<strong className='text-white'>Credentials:</strong> usernames
								and hashed passwords.
							</li>
							<li>
								<strong className='text-white'>
									Transactional & usage data:
								</strong>{' '}
								league/team/fixture records, in-app actions, preferences, audit
								logs.
							</li>
							<li>
								<strong className='text-white'>Device & network data:</strong>{' '}
								IP address, browser/device type, settings, diagnostic logs,
								telemetry.
							</li>
							<li>
								<strong className='text-white'>Communications:</strong> emails,
								support tickets, feedback, SMS metadata.
							</li>
							<li>
								<strong className='text-white'>Cookies & tracking:</strong> see
								Section 9 for details.
							</li>
						</ul>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							3) How We Use Information
						</h2>
						<ul className='space-y-4 mb-6 list-disc list-inside'>
							<li>
								Provide, maintain, and improve the CrikIT platform and related
								services.
							</li>
							<li>
								Authenticate users, secure accounts, and prevent fraud or abuse.
							</li>
							<li>
								Send transactional communications, alerts, and support
								responses.
							</li>
							<li>
								If you opt in: deliver SMS messages for verification, event
								updates, and support.
							</li>
							<li>Comply with legal obligations and enforce our agreements.</li>
						</ul>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							4) Legal Bases
						</h2>
						<p className='mb-6'>
							Where laws such as GDPR/UK GDPR apply, we rely on consent (e.g.,
							SMS opt in, certain cookies), contract performance, legitimate
							interests (security, product analytics), and legal obligations to
							process personal data.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							5) Sharing & Disclosure
						</h2>
						<p className='mb-6'>
							We do not sell personal data. We share limited information with
							vetted service providers (hosting, analytics, messaging, customer
							support) who process data on our behalf under contractual
							safeguards, or when required by law, to protect rights, or with
							your instruction.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							6) Data Retention
						</h2>
						<p className='mb-6'>
							We retain personal information only as long as necessary for the
							purposes described here or as required by law, then securely
							delete or anonymize it.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							7) Security
						</h2>
						<p className='mb-6'>
							We implement administrative, technical, and physical safeguards
							appropriate to the sensitivity of the data, including encryption
							in transit, access controls, least privilege practices, and
							monitoring. No method of transmission or storage is completely
							secure, but we strive to protect your data.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							8) Your Rights & Choices
						</h2>
						<p className='mb-6'>
							Depending on your location, you may have rights to access,
							correct, delete, export, or restrict processing of your personal
							data, object to certain uses, and withdraw consent. To exercise
							rights or request a copy, contact us at{' '}
							<a
								href={`mailto:${CONTACT_EMAIL}`}
								className='text-blue-400 hover:text-blue-300'>
								{CONTACT_EMAIL}
							</a>
							. We honor opt-out requests for marketing emails or SMS promptly.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							9) Cookies & Similar Technologies
						</h2>
						<p className='mb-6'>
							We use cookies and similar technologies to operate the site,
							remember preferences, and analyze usage. Where required, we seek
							consent before setting non-essential cookies. You can manage
							cookies through your browser; essential features may not function
							without them.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							10) Children’s Privacy
						</h2>
						<p className='mb-6'>
							The Services are not directed to children under 13 (or other age
							defined by local law). We do not knowingly collect personal
							information from children. If we discover such data, we will
							delete it.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							11) SMS Messaging (Privacy)
						</h2>
						<p className='mb-4'>
							When you opt in to Asmakam SMS programs, we process your mobile
							number and messaging metadata (delivery logs, timestamps) to send
							texts such as verification codes, service notices, schedule
							updates, and support messages.
						</p>
						<ul className='list-disc list-inside space-y-3'>
							<li>Message frequency varies by program.</li>
							<li>Msg & data rates may apply.</li>
							<li>
								Reply <span className='font-semibold text-white'>STOP</span> at
								any time to opt out, or{' '}
								<span className='font-semibold text-white'>HELP</span> for
								assistance.
							</li>
							<li>
								We do not sell your mobile number or SMS content. We share SMS
								data only with providers that help us deliver messages under
								contract, or when required by law.
							</li>
						</ul>
						<p className='mt-4'>
							See our{' '}
							<Link
								to='/terms'
								className='text-blue-400 underline hover:text-blue-300'>
								Terms & Conditions
							</Link>{' '}
							for program-level disclosures.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							12) International Transfers
						</h2>
						<p className='mb-6'>
							We may process or store personal data in the United States or
							other countries. When transferring data internationally, we use
							safeguards such as Standard Contractual Clauses as required by
							law.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							13) Changes to This Policy
						</h2>
						<p className='mb-6'>
							We may update this Privacy Policy to reflect changes in practices
							or legal requirements. We will update the "Last updated" date and,
							where appropriate, provide additional notice.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							14) Contact
						</h2>
						<p className='mb-6'>
							Questions about privacy or our SMS program? Email{' '}
							<a
								href={`mailto:${CONTACT_EMAIL}`}
								className='text-blue-400 hover:text-blue-300'>
								{CONTACT_EMAIL}
							</a>
							, call {SUPPORT_PHONE}, or write to Asmakam LLC,{' '}
							{BUSINESS_ADDRESS}.
						</p>
					</section>
				</div>

				<div className='mt-20 pt-10 border-t border-blue-900/30'>
					<h2 className='font-display text-2xl font-semibold tracking-tight mb-6 text-white'>
						Related
					</h2>
					<Link
						to='/terms'
						className='text-blue-400 underline hover:text-blue-300'>
						Terms & Conditions
					</Link>
				</div>
			</main>
			<Footer />
		</div>
	);
}
